<template>
  <div class="widget position-relative d-flex justify-content-between flex-column">
    <component
      :is="component"
      :title="title"
      :config="config"
      :type="type"
      :explicit-filter="filter"
      :filter-values="filterValues"
      @refresh="refresh"
    ></component>
    <i
      v-if="isLoading && !caching && !error"
      class="status-icon fa fa-fw fa-circle-o-notch fa-spin text-muted"
    ></i>
    <i
      v-if="caching"
      class="status-icon fa fa-fw fa-clock-o text-muted"
    ></i>
    <i
      v-if="error"
      class="status-icon fa fa-fw fa-exclamation-triangle text-danger"
      :title="error"
    ></i>
  </div>
</template>

<style lang="scss" scoped>
.status-icon {
  position: absolute;
  top: 15px;
  right: 30px;
}
</style>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { get, merge, filter, pickBy } from 'lodash'
import { loadWidget } from '@/api'
import { EventBus } from '@/lib/event-bus'
import axios from 'axios'
import Chart from './widgets/ChartWidget'
import Empty from './widgets/EmptyWidget'
import Health from './widgets/HealthWidget'
import Leaderboard from './widgets/LeaderboardWidget'
import Number from './widgets/NumberWidget'
import Table from './widgets/TableWidget'

const CancelToken = axios.CancelToken

export default {
  name: 'Widget',

  components: {
    Chart,
    Empty,
    Health,
    Leaderboard,
    Number,
    Table
  },

  props: {
    title: {
      type: String,
      default: null
    },

    description: {
      type: String,
      default: null
    },

    icon: {
      type: String,
      default: null
    },

    explore: {
      type: String,
      default: null
    },

    type: {
      type: String,
      default: null
    },

    value: {
      type: Object,
      default () {
        return null
      }
    },

    filter: {
      type: Object,
      default () {
        return {}
      }
    },

    set: {
      type: Object,
      default () {
        return {}
      }
    },

    inset: {
      type: Boolean,
      default: false
    },

    shadow: {
      type: Boolean,
      default: true
    },

    flush: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isLoading: false,
      caching: false,
      error: false,
      config: {
        component: 'Empty',
        data: {
          options: {}
        }
      },
      cancel: null
    }
  },

  computed: {
    ...mapGetters('dashboard', [
      'filterValues',
      'filterValuesByKeys'
    ]),

    component () {
      return get(this.config, 'component')
    },

    filters () {
      return get(this.config, 'filters', [])
    },

    filterKeys () {
      return this.filters.map(filter => filter.filter)
    }
  },

  watch: {
    value: {
      handler () {
        this.config = this.value
      },
      deep: true
    },

    filter () {
      this.loadConfig()
    },

    // When config is loaded, register filters
    config (val) {
      get(this.config, 'filters', []).forEach(filter => {
        this.registerFilter({
          filter
        })
      })
    }
  },

  mounted () {
    if (this.value) {
      this.applyConfig(this.value)
      EventBus.$on('updated-filters', () => {
        this.applyConfig(this.value)
      })
    } else {
      this.loadConfig()
      EventBus.$on('updated-filters', this.loadConfig)
    }
  },

  beforeDestroy () {
    EventBus.$off('updated-filters', this.loadConfig)
  },

  methods: {
    ...mapMutations('dashboard', [
      'registerFilter'
    ]),

    async loadConfig () {
      this.cancel && this.cancel('Previous request')
      if (this.type) {
        this.$parent.$emit('widget.config.loading', this)
        try {
          this.loading(true)
          const response = await loadWidget(this.type, merge({}, this.filterValues, this.filter), this.flush, new CancelToken((c) => {
            this.cancel = c
          }))
          this.applyConfig(response.data)
          this.loading(false)
        } catch (error) {
          if (error.response && error.response.status === 422) {
            this.caching = true
          } else if (!axios.isCancel(error)) {
            this.error = true
          }
        }
      } else {
        this.applyConfig({})
      }
    },

    loading (value) {
      if (value !== undefined) {
        this.isLoading = !!value
      } else {
        return this.isLoading
      }
    },

    applyConfig (config) {
      config.options = merge({
        inset: this.inset,
        shadow: this.shadow
      }, get(config, 'options') || {})

      this.config = merge({}, config, pickBy({
        title: this.title,
        explore: this.explore,
        icon: this.icon,
        description: this.description
      }), this.set)

      this.$parent.$emit('widget.config.updated', this)
    },

    refresh () {
      this.loadConfig()
    }
  }
}
</script>
